import { Injectable, inject } from '@angular/core';
import { Observable, ReplaySubject, Subject, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UserService } from "app/core/user/user.service";
import { applyBrandingStyles, removeBrandingStyles } from "./branding-styles";
import { OrganizationBranding } from "./branding.types";
import { getLocalBrandingForOrganization, getLocalBrandingForSubdomain } from "./local-branding.config";
import { environment } from "environments/environment";
import { normalizeOrgName } from "./org-normalize.util";

/** Get effective org name for branding from localStorage (user.organization.name or super_admin_organization_name). */
function getEffectiveOrganizationName(): string | null {
    const superAdminName = localStorage.getItem('super_admin_organization_name');
    if (superAdminName && superAdminName.trim()) return normalizeOrgName(superAdminName);
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        const user = JSON.parse(raw);
        const name = user?.organization?.name;
        return name && String(name).trim() ? normalizeOrgName(name) : null;
    } catch {
        return null;
    }
}

const STORAGE_KEY = 'organizationBranding';
const STORAGE_KEY_SUBDOMAIN_PREFIX = 'organizationBranding_subdomain_';

/**
 * Extract subdomain from hostname when on *.{domain} (e.g. zosmatlas.geoactivate.ai -> zosmatlas)
 * or *.localhost for local dev (e.g. zosmatlas.localhost -> zosmatlas).
 */
export function getSubdomainFromHost(): string | null {
    if (typeof window === 'undefined') return null;
    const host = window.location.hostname.toLowerCase();
    if (host.endsWith('.localhost') && host !== 'localhost') {
        const sub = host.slice(0, -'.localhost'.length);
        return sub && !sub.includes('.') ? sub : null;
    }
    const domains = environment.brandingDomains ?? (environment.brandingDomain ? [environment.brandingDomain] : ['geoactivate.com', 'geoactivate.ai']);
    for (const baseDomain of domains) {
        const d = baseDomain.toLowerCase().trim();
        if (host === d) return null;
        const suffix = `.${d}`;
        if (host.endsWith(suffix)) {
            const sub = host.slice(0, -suffix.length);
            if (sub && !sub.includes('.')) return sub;
        }
    }
    return null;
}

@Injectable({ providedIn: 'root' })
export class OrganizationBrandingService {
    private _userService = inject(UserService);
    private _branding: ReplaySubject<OrganizationBranding> = new ReplaySubject<OrganizationBranding>(1);
    private _destroyed = new Subject<void>();

    constructor() {
        this.loadBrandingFromLocalStorage();
        this._userService.user$.pipe(takeUntil(this._destroyed)).subscribe(() => {
            this.applyBrandingFromLocalConfig();
        });
    }

    get branding$(): Observable<OrganizationBranding> {
        return this._branding.asObservable();
    }

    /**
     * Apply branding from local config (org name / super_admin). No API call.
     */
    loadBranding(): Observable<OrganizationBranding> {
        this.applyBrandingFromLocalConfig();
        return of({ logoUrl: null } as OrganizationBranding);
    }

    /**
     * Load branding by subdomain for public login page (e.g. zosmatlas.geoactivate.ai).
     * Only uses local config (SUBDOMAIN_TO_ORG_NAME). No API call.
     */
    loadBrandingBySubdomain(subdomain: string): Observable<OrganizationBranding> {
        const key = `${STORAGE_KEY_SUBDOMAIN_PREFIX}${subdomain}`;
        const local = getLocalBrandingForSubdomain(subdomain);
        let cached = this.getCachedBranding(key);
        if (cached && local) {
            cached = {
                ...cached,
                logoDarkUrl: local.logoDarkUrl ?? cached.logoDarkUrl,
                logoUrl: local.logoUrl ?? cached.logoUrl,
                faviconUrl: local.faviconUrl ?? cached.faviconUrl,
                documentTitle: local.documentTitle ?? cached.documentTitle,
            };
            this._branding.next(cached);
            localStorage.setItem(key, JSON.stringify(cached));
            applyBrandingStyles(cached);
            return of(cached);
        }
        if (cached) {
            this._branding.next(cached);
            applyBrandingStyles(cached);
            return of(cached);
        }
        if (!local) {
            const defaultBranding: OrganizationBranding = { logoUrl: null };
            this._branding.next(defaultBranding);
            removeBrandingStyles();
            return of(defaultBranding);
        }
        if (local && (local.primaryColor ?? local.accentColor ?? local.primaryPalette ?? local.accentPalette ?? local.logoUrl)) {
            const branding: OrganizationBranding = {
                logoUrl: local.logoUrl ?? null,
                logoDarkUrl: local.logoDarkUrl ?? undefined,
                primaryColor: local.primaryColor ?? undefined,
                accentColor: local.accentColor ?? undefined,
                primaryPalette: local.primaryPalette ?? undefined,
                accentPalette: local.accentPalette ?? undefined,
                primaryBaseShade: local.primaryBaseShade ?? undefined,
                accentBaseShade: local.accentBaseShade ?? undefined,
                logoMaxWidth: local.logoMaxWidth ?? undefined,
                logoHeight: local.logoHeight ?? undefined,
                logoWidthPercent: local.logoWidthPercent ?? undefined,
                faviconUrl: local.faviconUrl ?? undefined,
                documentTitle: local.documentTitle ?? undefined,
                fontFamily: local.fontFamily ?? undefined,
            };
            this._branding.next(branding);
            localStorage.setItem(key, JSON.stringify(branding));
            applyBrandingStyles(branding);
            return of(branding);
        }
        const defaultBranding: OrganizationBranding = { logoUrl: null };
        this._branding.next(defaultBranding);
        removeBrandingStyles();
        return of(defaultBranding);
    }

    /**
     * Apply branding from frontend-only config.
     * Priority: (1) subdomain from URL (e.g. zosmatlas.localhost), (2) org name from localStorage.
     */
    applyBrandingFromLocalConfig(): void {
        const subdomain = getSubdomainFromHost();
        let local = subdomain ? getLocalBrandingForSubdomain(subdomain) : null;
        if (!local) {
            const orgName = getEffectiveOrganizationName();
            local = getLocalBrandingForOrganization(orgName);
        }
        if (local && (local.primaryColor ?? local.accentColor ?? local.primaryPalette ?? local.accentPalette ?? local.logoUrl)) {
            const branding: OrganizationBranding = {
                logoUrl: local.logoUrl ?? null,
                logoDarkUrl: local.logoDarkUrl ?? undefined,
                primaryColor: local.primaryColor ?? undefined,
                accentColor: local.accentColor ?? undefined,
                primaryPalette: local.primaryPalette ?? undefined,
                accentPalette: local.accentPalette ?? undefined,
                primaryBaseShade: local.primaryBaseShade ?? undefined,
                accentBaseShade: local.accentBaseShade ?? undefined,
                logoMaxWidth: local.logoMaxWidth ?? undefined,
                logoHeight: local.logoHeight ?? undefined,
                logoWidthPercent: local.logoWidthPercent ?? undefined,
                faviconUrl: local.faviconUrl ?? undefined,
                documentTitle: local.documentTitle ?? undefined,
                fontFamily: local.fontFamily ?? undefined,
            };
            this._branding.next(branding);
            applyBrandingStyles(branding);
        } else {
            removeBrandingStyles();
        }
    }

    /**
     * Clear current branding from memory and optionally from storage (e.g. on logout).
     */
    clearBranding(clearStorage = false): void {
        const defaultBranding: OrganizationBranding = { logoUrl: null };
        this._branding.next(defaultBranding);
        removeBrandingStyles();
        if (clearStorage) {
            localStorage.removeItem(STORAGE_KEY);
        }
    }

    private loadBrandingFromLocalStorage(): void {
        const cached = this.getCachedBranding(STORAGE_KEY);
        if (cached) {
            this._branding.next(cached);
        }
        this.applyBrandingFromLocalConfig();
    }

    private getCachedBranding(key: string): OrganizationBranding | null {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        try {
            return JSON.parse(raw) as OrganizationBranding;
        } catch {
            return null;
        }
    }
}
